/**
 * @module public/js/utils/goReasons
 * @summary Shared helpers for Go reason schema normalization and validation.
 */

export const LEGACY_REASON_MAP = {
  emailAction: {
    kind: 'email',
    behavior: { openEmailComposer: true, meetingBooked: false },
  },
  meetingInviteSent: {
    kind: 'check',
    legacyMap: 'meetingInviteSent',
    behavior: { openEmailComposer: false, meetingBooked: true },
  },
  eventInviteSent: { kind: 'check', legacyMap: 'eventInviteSent' },
  goOtherText: {
    kind: 'text',
    legacyMap: 'goOtherText',
    placeholder: 'Specify…',
  },
};

export const VALID_KINDS = new Set(['email', 'check', 'text']);
export const VALID_LEGACY = new Set([
  'meetingInviteSent',
  'eventInviteSent',
  'goOtherText',
]);

/**
 * Create a slug-like identifier for a Go reason.
 *
 * @param {string} value - Raw label text.
 * @returns {string} Slug string.
 */
export function slugifyReasonId(value) {
  return (
    `${value ?? ''}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'reason'
  );
}

/**
 * Generate a unique reason id scoped to the current list.
 *
 * @param {string} label - Display label to seed the id.
 * @param {Set<string>} existingIds - Existing ids to avoid collisions.
 * @returns {string} Unique reason id.
 */
export function createReasonId(label, existingIds) {
  const base = `r_${slugifyReasonId(label)}`;
  let next = base;
  let counter = 1;
  while (existingIds.has(next)) {
    next = `${base}_${(counter += 1)}`;
  }
  existingIds.add(next);
  return next;
}

/**
 * Normalize Go reasons into the current schema, including legacy migrations.
 *
 * @param {Array<object>} reasons - Raw reason objects from AppConfig/outcomes.
 * @param {object} [options]
 * @param {'runtime'|'admin'} [options.mode='runtime'] - Controls id handling.
 * @param {(message: string) => void} [options.warn=console.warn] - Warning callback.
 * @returns {Array<object>} Normalized reason objects.
 */
export function normalizeGoReasons(
  reasons,
  { mode = 'runtime', warn = console.warn } = {}
) {
  const seenIds = new Set();
  const normalized = [];
  let warnedDuplicate = false;

  (Array.isArray(reasons) ? reasons : []).forEach((reason, index) => {
    const isNewShape = reason && typeof reason === 'object' && 'id' in reason;
    const label = `${reason?.label ?? ''}`.trim();
    if (!label) return;

    const next = isNewShape
      ? {
          id: `${reason.id ?? ''}`.trim(),
          label,
          tooltip: `${reason.tooltip ?? ''}`.trim(),
          kind: `${reason.kind ?? ''}`.trim().toLowerCase(),
          enabled: true,
          legacyMap: `${reason.legacyMap ?? ''}`.trim(),
          placeholder: `${reason.placeholder ?? ''}`.trim(),
          behavior: reason.behavior ?? {},
        }
      : (() => {
          const legacyKey = `${reason?.key ?? ''}`.trim();
          const legacyDefaults = LEGACY_REASON_MAP[legacyKey] ?? {
            kind: 'check',
          };
          const legacyId =
            mode === 'admin'
              ? createReasonId(
                  label || legacyKey || `reason-${index + 1}`,
                  seenIds
                )
              : `legacy-${slugifyReasonId(legacyKey || label || `reason-${index + 1}`)}`;
          return {
            id: legacyId,
            label,
            tooltip: `${reason?.tooltip ?? ''}`.trim(),
            kind: legacyDefaults.kind,
            enabled: true,
            legacyMap: legacyDefaults.legacyMap,
            placeholder: legacyDefaults.placeholder ?? '',
            behavior: legacyDefaults.behavior ?? {},
          };
        })();

    if (!VALID_KINDS.has(next.kind)) next.kind = 'check';
    if (!VALID_LEGACY.has(next.legacyMap)) next.legacyMap = '';
    const hasExplicitMeetingBooked = Object.prototype.hasOwnProperty.call(
      next.behavior ?? {},
      'meetingBooked'
    );
    next.behavior = {
      openEmailComposer: next.behavior?.openEmailComposer === true,
      meetingBooked: hasExplicitMeetingBooked
        ? next.behavior?.meetingBooked === true
        : next.legacyMap === 'meetingInviteSent',
    };
    if (next.kind === 'text' && !next.placeholder) {
      next.placeholder = 'Specify…';
    }

    if (mode === 'admin') {
      if (!next.id || seenIds.has(next.id)) {
        next.id = createReasonId(label || `reason-${index + 1}`, seenIds);
      } else {
        seenIds.add(next.id);
      }
    } else {
      if (!next.id) {
        next.id = `legacy-${slugifyReasonId(label || `reason-${index + 1}`)}`;
      }
      if (seenIds.has(next.id)) {
        if (!warnedDuplicate) {
          warn(
            '[GoModal] Duplicate goReason id detected; ignoring later entries.'
          );
          warnedDuplicate = true;
        }
        return;
      }
      seenIds.add(next.id);
    }

    normalized.push(next);
  });

  return normalized;
}

/**
 * Validate Go reasons for admin save.
 *
 * @param {Array<object>} reasons - Normalized reason entries.
 * @returns {{valid: boolean, message: string, index: number}} Validation result.
 */
export function validateGoReasons(reasons) {
  const enabledReasons = reasons.filter((reason) => reason.enabled !== false);
  const missingLabelIndex = enabledReasons.findIndex(
    (reason) => `${reason.label ?? ''}`.trim().length === 0
  );
  if (missingLabelIndex >= 0) {
    return {
      valid: false,
      message: 'All enabled reasons must have a label.',
      index: missingLabelIndex,
    };
  }

  const legacyCounts = enabledReasons.reduce((acc, reason) => {
    if (reason.legacyMap) {
      acc[reason.legacyMap] = (acc[reason.legacyMap] || 0) + 1;
    }
    return acc;
  }, {});

  const legacyConflict = Object.values(legacyCounts).some((count) => count > 1);
  if (legacyConflict) {
    return {
      valid: false,
      message: 'Legacy mappings must be unique for enabled reasons.',
      index: -1,
    };
  }

  const textLegacyConflict = enabledReasons.some(
    (reason) => reason.legacyMap === 'goOtherText' && reason.kind !== 'text'
  );
  if (textLegacyConflict) {
    return {
      valid: false,
      message: 'Only text reasons can map to the Other legacy field.',
      index: -1,
    };
  }

  return { valid: true, message: '', index: -1 };
}
